"use client";

import { getSiteUiCopy } from "@/lib/site-ui-copy";

import Image from "next/image";
import Link from "@/components/ui/CcLink";
import { ChevronRight } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { CcLangSwitcher } from "@/components/layout/CcLangSwitcher";
import { CcMegaAllLink, CcMegaBackButton } from "@/components/ui/CcMegaAllLink";
import { isResourcesNavTriggerHref } from "@/lib/cc-nav-resources";
import type { NavResourceMegaColumn } from "@/lib/cc-nav-resources-mega";
import type { NavProjectMegaColumn } from "@/lib/cc-nav-projects-mega";
import type { NavServiceMegaColumn } from "@/lib/cc-nav-services-mega";
import type { NavServiceItem } from "@/lib/services";
import type { SiteLocale } from "@/lib/i18n/config";
import type { Lang } from "@/lib/types";
import { matchesLocalizedNavPath } from "@/lib/i18n/paths";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

/** Keep in sync with `$cc-nav-mobile-panel-duration` in `_nav-mobile-menu.scss`. */
const CC_NAV_MOBILE_MENU_EXIT_MS = 450;

type NavLink = { label: string; href: string };

type MobileMenuStackView = "root" | "services" | "resources" | "projects";

type CcNavMobileMenuProps = {
  open: boolean;
  onClose: () => void;
  links: NavLink[];
  serviceItems: NavServiceItem[];
  serviceMegaColumns: NavServiceMegaColumn[];
  compactServicesNav?: boolean;
  servicesSeeAllLabel: string;
  projectMegaColumns: NavProjectMegaColumn[];
  projectsSeeAllLabel: string;
  resourceMegaColumns: NavResourceMegaColumn[];
  resourcesSeeAllLabel: string;
  backLabel: string;
  isActive: (href: string) => boolean;
  locale: SiteLocale;
  lang?: Lang;
  /** Nav pill is over a dark hero — styles backdrop / chrome hints. */
  navOnDark?: boolean;
};

function CcNavMobileMenuRowLink({
  href,
  label,
  active,
  onNavigate,
  priority = "intent",
}: {
  href: string;
  label: string;
  active: boolean;
  onNavigate: () => void;
  priority?: "intent" | "primary";
}) {
  return (
    <Link
      href={href}
      priority={priority}
      className={`cc-nav-mobile-menu__row${active ? " cc-nav-mobile-menu__row--active" : ""}`}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
    >
      {label}
    </Link>
  );
}

function CcNavMobileDrillRow({
  label,
  active,
  onDrill,
}: {
  label: string;
  active: boolean;
  onDrill: () => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onDrill();
    }
  };

  return (
    <button
      type="button"
      className={`cc-nav-mobile-menu__row cc-nav-mobile-menu__row--drill${active ? " cc-nav-mobile-menu__row--active" : ""}`}
      onClick={onDrill}
      onKeyDown={handleKeyDown}
    >
      <span className="cc-nav-mobile-menu__row-label">{label}</span>
      <ChevronRight
        className="cc-nav-mobile-menu__chevron cc-nav-mobile-menu__chevron--forward"
        aria-hidden="true"
        strokeWidth={2}
        size={20}
      />
    </button>
  );
}

function CcNavMobileMegaSections({
  columns,
  isActive,
  onNavigate,
  seeAllHref,
  seeAllLabel,
  showFooterSeeAll = false,
}: {
  columns:
    | NavServiceMegaColumn[]
    | NavResourceMegaColumn[]
    | NavProjectMegaColumn[];
  isActive: (href: string) => boolean;
  onNavigate: () => void;
  seeAllHref: string;
  seeAllLabel: string;
  showFooterSeeAll?: boolean;
}) {
  return (
    <>
      {columns.map((column) => {
        const columnKey =
          "tag" in column ? column.tag : column.id;
        const headerHref = "headerHref" in column ? column.headerHref : null;

        return (
          <section key={columnKey} className="cc-nav-mobile-menu__group">
            {headerHref ? (
              <Link
                href={headerHref}
                className={`cc-nav-mobile-menu__group-title cc-nav-mobile-menu__group-title--link${isActive(headerHref) ? " cc-nav-mobile-menu__group-title--active" : ""}`}
                onClick={onNavigate}
              >
                {column.header}
              </Link>
            ) : (
              <h3 className="cc-nav-mobile-menu__group-title">{column.header}</h3>
            )}
            <ul className="cc-nav-mobile-menu__group-list">
              {column.links.map((link) => (
                <li key={link.key}>
                  <Link
                    href={link.href}
                    className={`cc-nav-mobile-menu__group-link${isActive(link.href) ? " cc-nav-mobile-menu__group-link--active" : ""}`}
                    onClick={onNavigate}
                  >
                    <span className="cc-nav-mobile-menu__group-link-text">
                      <span className="cc-nav-mobile-menu__group-link-label">
                        {link.label}
                      </span>
                      {link.description ? (
                        <span className="cc-nav-mobile-menu__group-link-desc">
                          {link.description}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      {showFooterSeeAll ? (
        <Link
          href={seeAllHref}
          className="cc-nav-mobile-menu__see-all"
          onClick={onNavigate}
        >
          {seeAllLabel}
        </Link>
      ) : null}
    </>
  );
}

export function CcNavMobileMenu({
  open,
  onClose,
  links,
  serviceItems,
  serviceMegaColumns,
  compactServicesNav = false,
  servicesSeeAllLabel,
  projectMegaColumns,
  projectsSeeAllLabel,
  resourceMegaColumns,
  resourcesSeeAllLabel,
  backLabel,
  isActive,
  locale,
  lang = locale,
  navOnDark = false,
}: CcNavMobileMenuProps) {
  const uiCopy = getSiteUiCopy(lang).navigation;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const backBtnRef = useRef<HTMLButtonElement>(null);
  const [mounted, setMounted] = useState(open);
  const [exiting, setExiting] = useState(false);
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const [stackView, setStackView] = useState<MobileMenuStackView>("root");

  const homeLink = links[0];
  const homeHref = homeLink?.href ?? "/";
  const navLinks = links.filter((link) => link.href !== homeHref);

  const servicesLink = navLinks.find((link) =>
    matchesLocalizedNavPath(link.href, locale, "services"),
  );
  const resourcesLink = navLinks.find((link) =>
    isResourcesNavTriggerHref(link.href, locale),
  );
  const projectsLink = navLinks.find((link) =>
    matchesLocalizedNavPath(link.href, locale, "projects"),
  );

  const subViewTitle =
    stackView === "services"
      ? (servicesLink?.label ?? uiCopy.services)
      : stackView === "resources"
        ? (resourcesLink?.label ?? uiCopy.resources)
        : stackView === "projects"
          ? (projectsLink?.label ?? uiCopy.projects)
          : "";

  const subBarSeeAll =
    stackView === "services" && servicesLink
      ? { href: servicesLink.href, label: servicesSeeAllLabel }
      : stackView === "resources" && resourcesLink
        ? { href: resourcesLink.href, label: resourcesSeeAllLabel }
        : stackView === "projects" && projectsLink
          ? { href: projectsLink.href, label: projectsSeeAllLabel }
          : null;

  const handleNavigate = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    setStackView("root");
  }, []);

  useEffect(() => {
    if (open) {
      setExiting(false);
      setMounted(true);
      closeBtnRef.current?.focus({ preventScroll: true });
      return;
    }

    if (!mounted) return;

    if (prefersReducedMotion) {
      setMounted(false);
      setExiting(false);
      setStackView("root");
      return;
    }

    setExiting(true);
  }, [open, mounted, prefersReducedMotion]);

  useEffect(() => {
    if (!exiting) return;

    const finishExit = () => {
      setMounted(false);
      setExiting(false);
      setStackView("root");
    };

    const panel = panelRef.current;
    if (!panel) {
      finishExit();
      return;
    }

    const onAnimationEnd = (event: AnimationEvent) => {
      if (
        event.target === panel &&
        event.animationName === "cc-nav-mobile-panel-out"
      ) {
        finishExit();
      }
    };

    panel.addEventListener("animationend", onAnimationEnd);
    const fallback = window.setTimeout(
      finishExit,
      CC_NAV_MOBILE_MENU_EXIT_MS + 80,
    );

    return () => {
      panel.removeEventListener("animationend", onAnimationEnd);
      window.clearTimeout(fallback);
    };
  }, [exiting]);

  useEffect(() => {
    if (!open || stackView === "root") return;
    // Keep focus accessible without scrolling the offscreen pane into view mid-slide.
    backBtnRef.current?.focus({ preventScroll: true });
  }, [open, stackView]);

  useEffect(() => {
    if (!mounted) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (stackView !== "root") {
          setStackView("root");
        } else {
          onClose();
        }
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const visibleFocusable = Array.from(focusable).filter(
        (element) =>
          element.offsetParent !== null &&
          !element.closest("[inert]") &&
          !element.hasAttribute("inert"),
      );
      if (visibleFocusable.length === 0) return;

      const first = visibleFocusable[0];
      const last = visibleFocusable[visibleFocusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, stackView]);

  if (!mounted) {
    return null;
  }

  const isSubView = stackView !== "root";

  return (
    <div
      className={`cc-nav-mobile-menu${navOnDark ? " cc-nav-mobile-menu--nav-on-dark" : ""}${exiting ? " cc-nav-mobile-menu--closing" : ""}`}
      role="presentation"
      aria-hidden={exiting}
    >
      <button
        type="button"
        className="cc-nav-mobile-menu__bg"
        aria-label={uiCopy.closeMenu}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        id="cc-nav-mobile-menu"
        className="cc-nav-mobile-menu__panel"
        role="dialog"
        aria-modal="true"
        aria-label={isSubView ? subViewTitle : uiCopy.main}
      >
        <header className="cc-nav-mobile-menu__header">
          <div className="cc-nav-mobile-menu__header-row">
            <Link
              href={homeHref}
              priority="primary"
              className="cc-nav-mobile-menu__brand"
              aria-label={uiCopy.home}
              onClick={handleNavigate}
            >
              <Image
                src="/assets/imgs/nav-mark.svg"
                alt=""
                width={20}
                height={21}
                className="cc-nav-mobile-menu__brand-mark"
              />
            </Link>
            <button
              ref={closeBtnRef}
              type="button"
              className="cc-nav-mobile-menu__close"
              aria-label={uiCopy.closeMenu}
              onClick={onClose}
            >
              <span className="cc-nav-mobile-menu__close-line cc-nav-mobile-menu__close-line--1" />
              <span className="cc-nav-mobile-menu__close-line cc-nav-mobile-menu__close-line--2" />
            </button>
          </div>
        </header>

        <div className="cc-nav-mobile-menu__stack">
          <div
            className={`cc-nav-mobile-menu__stack-track${isSubView ? " cc-nav-mobile-menu__stack-track--sub" : ""}`}
          >
            <div
              className="cc-nav-mobile-menu__stack-slide cc-nav-mobile-menu__stack-slide--root"
              inert={isSubView}
              aria-hidden={isSubView}
            >
              <div className="cc-nav-mobile-menu__stack-slide-inner">
            <div
              className="cc-nav-mobile-menu__scroll"
              data-lenis-prevent
            >
              <nav className="cc-nav-mobile-menu__nav" aria-label={uiCopy.main}>
                {homeLink ? (
                  <CcNavMobileMenuRowLink
                    href={homeLink.href}
                    label={homeLink.label}
                    active={isActive(homeLink.href)}
                    onNavigate={handleNavigate}
                    priority="primary"
                  />
                ) : null}
                {navLinks.map((link) => {
                  if (matchesLocalizedNavPath(link.href, locale, "services")) {
                    const active =
                      isActive(link.href) ||
                      (compactServicesNav
                        ? serviceItems.some((item) => isActive(item.href))
                        : serviceMegaColumns.some(
                            (column) =>
                              isActive(column.headerHref) ||
                              column.links.some((item) => isActive(item.href)),
                          ));

                    return (
                      <CcNavMobileDrillRow
                        key={link.href}
                        label={link.label}
                        active={active}
                        onDrill={() => setStackView("services")}
                      />
                    );
                  }

                  if (isResourcesNavTriggerHref(link.href, locale)) {
                    const active = resourceMegaColumns.some((column) =>
                      column.links.some((item) => isActive(item.href)),
                    );

                    return (
                      <CcNavMobileDrillRow
                        key={link.href}
                        label={link.label}
                        active={active}
                        onDrill={() => setStackView("resources")}
                      />
                    );
                  }

                  if (matchesLocalizedNavPath(link.href, locale, "projects")) {
                    const active =
                      isActive(link.href) ||
                      projectMegaColumns.some((column) =>
                        column.links.some((item) => isActive(item.href)),
                      );

                    return (
                      <CcNavMobileDrillRow
                        key={link.href}
                        label={link.label}
                        active={active}
                        onDrill={() => setStackView("projects")}
                      />
                    );
                  }

                  return (
                    <CcNavMobileMenuRowLink
                      key={link.href}
                      href={link.href}
                      label={link.label}
                      active={isActive(link.href)}
                      onNavigate={handleNavigate}
                      priority="primary"
                    />
                  );
                })}
              </nav>
            </div>

            <footer className="cc-nav-mobile-menu__footer">
              <CcLangSwitcher
                className="cc-nav-mobile-menu__lang"
                variant="popover"
              />
            </footer>
              </div>
            </div>

            <div
              className="cc-nav-mobile-menu__stack-slide cc-nav-mobile-menu__stack-slide--sub"
              inert={!isSubView}
              aria-hidden={!isSubView}
            >
            <div className="cc-nav-mobile-menu__sub-bar">
              <div className="cc-nav-mobile-menu__sub-bar-row">
                <CcMegaBackButton
                  ref={backBtnRef}
                  className="cc-nav-mobile-menu__sub-back"
                  aria-label={backLabel}
                  onClick={handleBack}
                >
                  {backLabel}
                </CcMegaBackButton>
                {subBarSeeAll ? (
                  <CcMegaAllLink
                    href={subBarSeeAll.href}
                    className="cc-nav-mobile-menu__sub-see-all"
                    onNavigate={handleNavigate}
                  >
                    {subBarSeeAll.label}
                  </CcMegaAllLink>
                ) : null}
              </div>
              <h2 className="cc-nav-mobile-menu__sub-title">{subViewTitle}</h2>
            </div>
            <div
              className="cc-nav-mobile-menu__scroll"
              data-lenis-prevent
            >
              <nav
                className="cc-nav-mobile-menu__nav cc-nav-mobile-menu__nav--sub"
                aria-label={subViewTitle}
              >
                {stackView === "services" && servicesLink ? (
                  compactServicesNav ? (
                    <>
                      {serviceItems.map((item) => (
                        <CcNavMobileMenuRowLink
                          key={item.href}
                          href={item.href}
                          label={item.label}
                          active={isActive(item.href)}
                          onNavigate={handleNavigate}
                        />
                      ))}
                      <Link
                        href={servicesLink.href}
                        className="cc-nav-mobile-menu__see-all"
                        onClick={handleNavigate}
                      >
                        {servicesSeeAllLabel}
                      </Link>
                    </>
                  ) : (
                    <CcNavMobileMegaSections
                      columns={serviceMegaColumns}
                      isActive={isActive}
                      onNavigate={handleNavigate}
                      seeAllHref={servicesLink.href}
                      seeAllLabel={servicesSeeAllLabel}
                    />
                  )
                ) : null}

                {stackView === "resources" && resourcesLink ? (
                  <CcNavMobileMegaSections
                    columns={resourceMegaColumns}
                    isActive={isActive}
                    onNavigate={handleNavigate}
                    seeAllHref={resourcesLink.href}
                    seeAllLabel={resourcesSeeAllLabel}
                  />
                ) : null}

                {stackView === "projects" && projectsLink ? (
                  <CcNavMobileMegaSections
                    columns={projectMegaColumns}
                    isActive={isActive}
                    onNavigate={handleNavigate}
                    seeAllHref={projectsLink.href}
                    seeAllLabel={projectsSeeAllLabel}
                  />
                ) : null}
              </nav>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
